import type { VideoAssetDto } from "../api/videoService";

/**
 * 段账本 —— chunk 控制条(续渲 / 合成成片 / 那句提示)的**单一真源**。
 *
 * ═══ 为什么不再钉 `task.status` ═══════════════════════════════════════════════
 * 因为它正是会被算错的那个字段。2026-08-19 老板走查真单 5219008119809 实测:
 * 4 段的片子第 3 段被内容审核拒 → 整单 PARTIAL,而**第 4 段从此再没被提交过**
 * (串行链失败即停,本来靠 PARTIAL + 「继续生成」兜底)。用户点「重渲第 3 段」,ai 的
 * `regenerate_chunk` 渲成后无条件发 `status=SUCCESS` —— 它说的是「这次重渲成功了」,
 * 而那个字段是**整单终态**。落库结果:
 *
 *     task.status = SUCCESS      而      CHUNK 4 = PENDING      且      没有 FINAL
 *
 * 此刻页面上 `canResume = task.status === "PARTIAL"` 落空 → **「继续生成」按钮整个不渲染**,
 * 提示掉进 else 分支显示「段生成中…」而根本没有人在渲它。唯一能补齐的入口消失,任务却写着
 * 「已完成」—— 用户被彻底卡死。
 *
 * 段行是 **ai 在渲染开始前下发的 planned 事实**(见 `VideoAssetDto.status` 注释:「分块计划先于
 * 渲染下发,段数一定就落 N 条 PENDING」),它不会因为某次回调说错话而改变。所以判据钉它。
 *
 * ★backend 侧有对称的一道(`MediaCompletionService.hasUndeliveredChunk`,把这种 SUCCESS 降回
 *   PARTIAL)。两层各自独立成立:即使哪天回调又算错一次,这一层也不该跟着塌。
 *
 * ★「已交付」口径与 backend `listSuccessChunks` / `hasUndeliveredChunk` 对齐:
 *   status=SUCCESS **且**拿得到产物地址。状态字段写着 SUCCESS 却没有 url 的不算 ——
 *   否则真缺的段会被伪装成已完成。
 */
export interface ChunkTally {
  /** 这次划分计划了几段(段行总数,不论状态)。 */
  planned: number;
  /** 已经能拿到产物的段数。 */
  delivered: number;
  /** 还没拿到产物的段数(= planned - delivered)。 */
  missing: number;
  /** 段全部出齐 → 可以(重新)合成成片。 */
  allReady: boolean;
  /**
   * 部分完成 = 出过一些、还缺一些、**且此刻没有段在渲**。
   *
   * 三个条件缺一不可:少了「出过一些」会把整单失败也引去续渲(那该走整体重试);
   * 少了「没有段在渲」会在正常首渲的中途就催用户点「继续生成」。
   */
  partial: boolean;
  /** 有段正在渲(RUNNING)—— 链还活着,等它就行,不该催用户动手。 */
  inFlight: boolean;
}

/**
 * 该段是否**拿得到东西**(与 backend 同口径:SUCCESS 且产物地址非空)。
 *
 * ★本仓「段已交付」的**唯一判据**(0926 S2-6):`partialResumeNotice` 数「渲好几段」也走这一个函数。
 *   此前它只数 `status === "SUCCESS"`,与这里的 url 口径并存 —— 同一页上控制条说「已生成 1/3 段」、
 *   提示却说「前面 2 段已经渲好了」。两处必须同一个数。
 */
export function isDeliveredChunk(a: { status: string; videoUrl?: string | null }): boolean {
  return a.status === "SUCCESS" && !!a.videoUrl && a.videoUrl.trim().length > 0;
}

export function tallyChunks(chunks: VideoAssetDto[] | null | undefined): ChunkTally {
  const list = chunks ?? [];
  const planned = list.length;
  const delivered = list.filter(isDeliveredChunk).length;
  const missing = planned - delivered;
  const inFlight = list.some((a) => a.status === "RUNNING");
  return {
    planned,
    delivered,
    missing,
    allReady: planned > 0 && missing === 0,
    partial: delivered > 0 && missing > 0 && !inFlight,
    inFlight,
  };
}
